import React, { useState } from 'react';
import { Modal, Form, Input, InputNumber, Alert, Row, Col, Divider, Button } from 'antd';
import { FormattedMessage, useIntl } from 'react-intl';
import { InfoCircleOutlined } from '@ant-design/icons';
import { Organization } from "../../api-client";
import { getInvoicesApi } from '../../api/apiInstances';
import logger from '../../utils/logger';

interface InvoiceConfigSetupModalProps {
    visible: boolean;
    onSuccess: () => void;
    onCancel: () => void;
    organization: Organization;
}

const InvoiceConfigSetupModal: React.FC<InvoiceConfigSetupModalProps> = ({
    visible,
    onSuccess,
    onCancel,
    organization
}) => {
    const intl = useIntl();
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const invoiceConfigApi = getInvoicesApi();

    const handleOk = async () => {
        try {
            setError(null);
            await form.validateFields();
            const values = form.getFieldsValue();

            setLoading(true);

            // Create both invoice and proforma invoice configs
            const configs = [
                {
                    type: 'INVOICE',
                    prefix: values.invoicePrefix || '',
                    startingNumber: values.invoiceStartingNumber || 1,
                    digitsCount: values.invoiceDigitsCount || 10
                },
                {
                    type: 'PROFORMA_INVOICE',
                    prefix: values.proformaPrefix || '',
                    startingNumber: values.proformaStartingNumber || 1,
                    digitsCount: values.proformaDigitsCount || 10
                }
            ];

            // Create both configs
            for (const config of configs) {
                const paddedNumber = String(config.startingNumber).padStart(config.digitsCount, '0');
                const formattedNumber = `${config.prefix}${paddedNumber}`;

                const invoiceConfig = {
                    prefix: config.prefix,
                    lastNumber: parseInt(String(config.startingNumber), 10),
                    digitsCount: parseInt(String(config.digitsCount), 10),
                    documentType: config.type,
                    organizationId: organization.id,
                    startingNumber: String(config.startingNumber)
                };

                await invoiceConfigApi.createOrUpdateInvoiceConfig(invoiceConfig);
            }

            form.resetFields();
            onSuccess();
        } catch (err: any) {
            logger.error('Error creating invoice configuration:', err);
            setError(err?.response?.data?.detail || intl.formatMessage({ id: 'error_creating_invoice_config' }));
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        form.resetFields();
        setError(null);
        onCancel();
    };

    // Constants for default values
    const DEFAULT_STARTING_NUMBER = 1;
    const DEFAULT_DIGITS_COUNT = 10;
    const DEFAULT_INVOICE_PREFIX = 'INV';
    const DEFAULT_PROFORMA_PREFIX = 'PROF';

    return (
        <Modal
            title={<div style={{ textAlign: 'center', paddingTop: '12px', paddingBottom: '12px' }}><FormattedMessage id="invoice_config_setup_title" /></div>}
            open={visible}
            onOk={handleOk}
            onCancel={handleCancel}
            confirmLoading={loading}
            okText={<FormattedMessage id="save" />}
            cancelText={<FormattedMessage id="cancel" />}
            width={650}
            destroyOnClose
            bodyStyle={{ padding: '0 24px' }}
            footer={
                <div style={{ padding: '10px 24px', display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                    <Button key="cancel" onClick={handleCancel}>
                        <FormattedMessage id="cancel" />
                    </Button>
                    <Button key="submit" type="primary" loading={loading} onClick={handleOk}>
                        <FormattedMessage id="save" />
                    </Button>
                </div>
            }
        >
            <Alert
                message={<FormattedMessage id="invoice_config_setup_description" />}
                type="info"
                icon={<InfoCircleOutlined />}
                showIcon
                style={{ marginBottom: 24 }}
            />

            {error && (
                <Alert
                    message={error}
                    type="error"
                    closable
                    onClose={() => setError(null)}
                    style={{ marginBottom: 16 }}
                />
            )}

            <Form
                form={form}
                layout="vertical"
                initialValues={{
                    invoicePrefix: DEFAULT_INVOICE_PREFIX,
                    invoiceStartingNumber: DEFAULT_STARTING_NUMBER,
                    invoiceDigitsCount: DEFAULT_DIGITS_COUNT,
                    proformaPrefix: DEFAULT_PROFORMA_PREFIX,
                    proformaStartingNumber: DEFAULT_STARTING_NUMBER,
                    proformaDigitsCount: DEFAULT_DIGITS_COUNT
                }}
            >
                {/* Invoice Configuration */}
                <Divider orientation="left" style={{ marginTop: 0 }}>
                    <strong><FormattedMessage id="invoice_configuration" /></strong>
                </Divider>

                <Row gutter={16}>
                    <Col span={8}>
                        <Form.Item
                            name="invoicePrefix"
                            label={<FormattedMessage id="invoice_config_prefix" />}
                            extra={<div style={{ whiteSpace: 'nowrap', fontSize: '12px' }}><FormattedMessage id="invoice_config_prefix_help" /></div>}
                            style={{ marginBottom: 24 }}
                        >
                            <Input
                                placeholder={intl.formatMessage({ id: 'invoice_config_prefix_placeholder' })}
                                maxLength={10}
                                size="large"
                            />
                        </Form.Item>
                    </Col>
                    <Col span={8}>
                        <Form.Item
                            name="invoiceStartingNumber"
                            label={<FormattedMessage id="invoice_config_starting_number" />}
                            rules={[
                                { required: true, message: <FormattedMessage id="required_field" /> },
                                { type: 'number', min: 1, message: <FormattedMessage id="invoice_config_starting_number_min" /> }
                            ]}
                            style={{ marginBottom: 24 }}
                        >
                            <InputNumber
                                style={{ width: '100%' }}
                                size="large"
                                min={1}
                                placeholder={String(DEFAULT_STARTING_NUMBER)}
                            />
                        </Form.Item>
                    </Col>
                    <Col span={8}>
                        <Form.Item
                            name="invoiceDigitsCount"
                            label={<FormattedMessage id="invoice_config_digits_count" />}
                            rules={[
                                { required: true, message: <FormattedMessage id="required_field" /> },
                                { type: 'number', min: 6, max: 15, message: <FormattedMessage id="invoice_config_digits_count_range" /> }
                            ]}
                            style={{ marginBottom: 24 }}
                        >
                            <InputNumber
                                style={{ width: '100%' }}
                                size="large"
                                min={6}
                                max={15}
                                placeholder={String(DEFAULT_DIGITS_COUNT)}
                            />
                        </Form.Item>
                    </Col>
                </Row>

                <Alert
                    message={<FormattedMessage id="invoice_config_example" />}
                    description={
                        <div style={{ fontSize: '13px', lineHeight: '1.4', marginTop: '4px' }}>
                            <FormattedMessage id="invoice_config_example_text" />
                            <br />
                            <strong style={{ fontSize: '14px', color: '#52c41a', fontWeight: 600, marginTop: '4px', display: 'inline-block' }}>
                                {form.getFieldValue('invoicePrefix') || DEFAULT_INVOICE_PREFIX}
                                {String(form.getFieldValue('invoiceStartingNumber') || DEFAULT_STARTING_NUMBER)
                                    .padStart(form.getFieldValue('invoiceDigitsCount') || DEFAULT_DIGITS_COUNT, '0')}
                            </strong>
                        </div>
                    }
                    type="success"
                    showIcon
                    style={{ marginBottom: 40, padding: '8px 12px' }}
                />

                {/* Proforma Invoice Configuration */}
                <Divider orientation="left">
                    <strong><FormattedMessage id="proforma_invoice_configuration" /></strong>
                </Divider>

                <Row gutter={16}>
                    <Col span={8}>
                        <Form.Item
                            name="proformaPrefix"
                            label={<FormattedMessage id="invoice_config_prefix" />}
                            extra={<div style={{ whiteSpace: 'nowrap', fontSize: '12px' }}><FormattedMessage id="invoice_config_prefix_help" /></div>}
                            style={{ marginBottom: 24 }}
                        >
                            <Input
                                placeholder={intl.formatMessage({ id: 'invoice_config_prefix_placeholder' })}
                                maxLength={10}
                                size="large"
                            />
                        </Form.Item>
                    </Col>
                    <Col span={8}>
                        <Form.Item
                            name="proformaStartingNumber"
                            label={<FormattedMessage id="invoice_config_starting_number" />}
                            rules={[
                                { required: true, message: <FormattedMessage id="required_field" /> },
                                { type: 'number', min: 1, message: <FormattedMessage id="invoice_config_starting_number_min" /> }
                            ]}
                            style={{ marginBottom: 24 }}
                        >
                            <InputNumber
                                style={{ width: '100%' }}
                                size="large"
                                min={1}
                                placeholder={String(DEFAULT_STARTING_NUMBER)}
                            />
                        </Form.Item>
                    </Col>
                    <Col span={8}>
                        <Form.Item
                            name="proformaDigitsCount"
                            label={<FormattedMessage id="invoice_config_digits_count" />}
                            rules={[
                                { required: true, message: <FormattedMessage id="required_field" /> },
                                { type: 'number', min: 6, max: 15, message: <FormattedMessage id="invoice_config_digits_count_range" /> }
                            ]}
                            style={{ marginBottom: 24 }}
                        >
                            <InputNumber
                                style={{ width: '100%' }}
                                size="large"
                                min={6}
                                max={15}
                                placeholder={String(DEFAULT_DIGITS_COUNT)}
                            />
                        </Form.Item>
                    </Col>
                </Row>

                <Alert
                    message={<FormattedMessage id="invoice_config_example" />}
                    description={
                        <div style={{ fontSize: '13px', lineHeight: '1.4', marginTop: '4px' }}>
                            <FormattedMessage id="invoice_config_example_text" />
                            <br />
                            <strong style={{ fontSize: '14px', color: '#52c41a', fontWeight: 600, marginTop: '4px', display: 'inline-block' }}>
                                {form.getFieldValue('proformaPrefix') || DEFAULT_PROFORMA_PREFIX}
                                {String(form.getFieldValue('proformaStartingNumber') || DEFAULT_STARTING_NUMBER)
                                    .padStart(form.getFieldValue('proformaDigitsCount') || DEFAULT_DIGITS_COUNT, '0')}
                            </strong>
                        </div>
                    }
                    type="success"
                    showIcon
                    style={{ padding: '8px 12px' }}
                />
            </Form>
        </Modal>
    );
};

export default InvoiceConfigSetupModal;
